<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { createAiCreditPurchaseRecovery } from '../../utils/aiCreditPurchaseRecovery.mjs';

const props = defineProps({
    // Opens on the pack list instead of the overview, for entry points whose
    // only purpose is buying more usage.
    startInPurchase: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const loading = ref(true);
const plan = ref('basic');
const included = ref({ used_percent: 0, resets_in_days: null });
const purchased = ref({ used_percent: 0, has_credits: false });
const bySpecialist = ref({});
const recentLogs = ref([]);
const credits = ref({ balance: 0, expires_next: null });
const canPurchase = ref(false);
const isCompanyAdmin = ref(false);
const packs = ref([]);
const packsLoaded = ref(false);
const serverLimitReached = ref(false);
const purchasingPack = ref(null);
const pendingPack = ref(null);
const purchaseMessage = ref('');
const purchaseError = ref('');
const purchaseSection = ref(null);
const unresolvedPurchase = ref(null);
const recoveryReady = ref(false);
const purchaseCompleted = ref(false);
const completedPurchase = ref(null);
const confettiKey = ref(0);
const hasStripeCustomer = ref(false);
const openingBilling = ref(false);
const billingError = ref('');
let purchaseRecovery = null;

// Every share below arrives from the server already reduced to a percentage.
// The euro figures behind them stay on the server: a cost sitting next to its
// token counts is enough to recover a provider's per million pricing, and a
// credit balance next to its euro value gives away the pack margin.
const includedPercentage = computed(() => Number(included.value.used_percent ?? 0));

// Whole days to the UTC start of next month, counted on the server so the
// countdown does not shift with the viewer's own clock or timezone.
const resetsInDays = computed(() => {
    if (includedPercentage.value === 0) {return null}

    const days = Number(included.value.resets_in_days);

    return Number.isFinite(days) && days > 0 ? days : null;
});

const purchasedPercentage = computed(() => Number(purchased.value.used_percent ?? 0));

const hasPurchasedUsage = computed(() => Boolean(purchased.value.has_credits));

const barColor = (percentage) => {
    if (percentage >= 80) return 'bg-myflowRed';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-myflowPurple';
};

const barTrackColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-100';
    if (percentage >= 50) return 'bg-yellow-100';
    return 'bg-myflowPurple-light';
};

const limitReached = computed(() => serverLimitReached.value);

const usedPercent = (percentage) => Math.min(Math.max(percentage, 0), 100).toFixed(0);

const pct = (percentage) => Number(percentage ?? 0).toFixed(1) + '%';

const specialistNames = {
    stella: _mft('dashboard:aiSpecialists.stella.name'),
    maya: _mft('dashboard:aiSpecialists.maya.name'),
    alex: _mft('dashboard:aiSpecialists.alex.name'),
    susan: _mft('dashboard:aiSpecialists.susan.name'),
    hugo: _mft('dashboard:aiSpecialists.oscar.name'),
    oscar: _mft('dashboard:aiSpecialists.oscar.name'),
    tom: _mft('dashboard:aiSpecialists.tom.name'),
    nina: _mft('dashboard:aiSpecialists.nina.name'),
    leo: _mft('dashboard:aiSpecialists.leo.name'),
    flowie: _mft('dashboard:aiSpecialists.flowie.name'),
};

const specialistDescriptions = {
    stella: _mft('dashboard:aiSpecialists.stella.role'),
    maya: _mft('dashboard:aiSpecialists.maya.role'),
    alex: _mft('dashboard:aiSpecialists.alex.role'),
    susan: _mft('dashboard:aiSpecialists.susan.role'),
    hugo: _mft('dashboard:aiSpecialists.oscar.role'),
    oscar: _mft('dashboard:aiSpecialists.oscar.role'),
    tom: _mft('dashboard:aiSpecialists.tom.role'),
    nina: _mft('dashboard:aiSpecialists.nina.role'),
    leo: _mft('dashboard:aiSpecialists.leo.role'),
    flowie: '',
};

const specialistIcons = {
    stella: 'fa-light fa-palette',
    maya: 'fa-light fa-pen-nib',
};

const specialistAvatars = {
    stella: 'ai-agent-web-designer-avatar',
    maya: 'ai-agent-content-creator-avatar',
    alex: 'ai-agent-newsletter-specialist-avatar',
    susan: 'ai-agent-data-analyst-avatar',
    hugo: 'ai-agent-lead-finder-avatar',
    oscar: 'ai-agent-lead-finder-avatar',
    tom: 'ai-agent-relationship-manager-avatar',
    nina: 'ai-agent-sales-assistant-avatar',
    leo: 'ai-agent-automation-expert-avatar',
    flowie: 'ai-agent-ai-orchestrator-avatar',
};

const showPurchaseSection = ref(props.startInPurchase);

const specialists = computed(() => {
    return Object.entries(bySpecialist.value).map(([key, data]) => ({
        key,
        name: specialistNames[key] || key,
        description: specialistDescriptions[key] || '',
        icon: specialistIcons[key] || 'fa-light fa-robot',
        avatar: specialistAvatars[key] || 'span',
        percentage: Number(data.share_of_usage_percent ?? 0).toFixed(0),
        ofLimit: Number(data.share_of_capacity_percent ?? 0).toFixed(1),
        ...data,
    }));
});

const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return diffMins + 'm ago';
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return diffHours + 'h ago';
    const diffDays = Math.floor(diffHours / 24);
    return diffDays + 'd ago';
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(dateStr));
};

const formatCurrency = (amountCents, currency) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: String(currency || 'eur').toUpperCase(),
    }).format(amountCents / 100);

// The notice names the pack and its expiry, but only when both are known: a
// purchase resolved from another tab can land before the pack list is loaded.
const completedPurchaseArgs = computed(() => {
    const packKey = completedPurchase.value?.pack_key;
    const expiresAt = completedPurchase.value?.expires_at;
    if (!packKey || !expiresAt) return null;

    const pack = packs.value.find((p) => p.key === packKey);
    if (!pack) return null;

    return { pack: pack.name, date: formatDate(expiresAt) };
});

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

async function fetchPacks(force = false) {
    if ((!force && packsLoaded.value) || !isCompanyAdmin.value) return;

    const response = await fetch(route('boIndex.aiCredits.packs'), {
        headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to load purchase options');

    const data = await response.json();
    packs.value = Array.isArray(data.packs) ? data.packs : [];
    canPurchase.value = Boolean(data.can_purchase);
    hasStripeCustomer.value = Boolean(data.has_stripe_customer);
    if (!purchaseRecovery) {
        purchaseRecovery = createAiCreditPurchaseRecovery({
            storage: window.localStorage,
            scope: data.purchase_scope,
            readStatus: async (id) => {
                const response = await fetch(route('boIndex.aiCredits.status', id), {
                    headers: { Accept: 'application/json' },
                });
                if (response.status === 404) return null;
                return purchaseResult(response);
            },
            submit: async (request) => purchaseResult(
                await fetch(route('boIndex.aiCredits.store'), {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken(),
                    },
                    body: JSON.stringify({ pack: request.pack, client_request_id: request.client_request_id }),
                }),
            ),
        });
        unresolvedPurchase.value = purchaseRecovery.load();
        recoveryReady.value = true;
    }
    packsLoaded.value = true;
}

async function purchaseResult(response) {
    const data = await response.json();
    if (!response.ok && !(response.status === 422 && data.status === 'failed')) {
        throw new Error('Purchase status unavailable');
    }
    return data;
}

async function updatePurchaseResult(result) {
    unresolvedPurchase.value = purchaseRecovery.load();
    if (!result) return;
    if (result.status === 'succeeded') {
        // Nothing is left to do in the purchase flow, so drop back to the
        // initial modal state and let the notice report the outcome there.
        purchaseMessage.value = '';
        purchaseError.value = '';
        pendingPack.value = null;
        showPurchaseSection.value = false;
        purchaseCompleted.value = true;
        completedPurchase.value = result;
        // Remount the confetti so a second purchase celebrates as well.
        confettiKey.value += 1;
        await fetchData();
    } else if (result.status === 'failed') {
        purchaseError.value = result.message || _mft('dashboard:aiSpend.purchaseFailed');
    } else if (result.status === 'needs_review') {
        purchaseMessage.value = _mft('dashboard:aiSpend.purchaseReview');
    } else {
        purchaseMessage.value = _mft('dashboard:aiSpend.purchasePending');
    }
}

async function recoverPurchase(retryMissing = false) {
    if (!purchaseRecovery || purchasingPack.value !== null || isUnmounted) return;
    purchasingPack.value = 'recovery';
    try {
        unresolvedPurchase.value = purchaseRecovery.load();
        if (!unresolvedPurchase.value) return;
        purchaseMessage.value = _mft('dashboard:aiSpend.checkingPurchase');
        purchaseError.value = '';
        const result = await purchaseRecovery.recover({ retryMissing });
        await updatePurchaseResult(result);
        return result;
    } catch {
        purchaseError.value = _mft('dashboard:aiSpend.purchaseRecoveryFailed');
    } finally {
        purchasingPack.value = null;
    }
}

function requestPurchase(pack) {
    if (!recoveryReady.value || purchasingPack.value !== null) return;
    purchaseError.value = '';
    purchaseMessage.value = '';
    pendingPack.value = pack;
}

function cancelPendingPurchase() {
    // Never drop the confirmation step while a charge is in flight.
    if (purchasingPack.value !== null) return;
    pendingPack.value = null;
}

async function confirmPendingPurchase() {
    if (!pendingPack.value) return;
    await purchasePack(pendingPack.value);
}

async function purchasePack(pack) {
    if (!recoveryReady.value || purchasingPack.value !== null) return;
    purchasingPack.value = pack.key;
    purchaseError.value = '';
    purchaseMessage.value = '';
    try {
        // Re-read storage in case another tab started a purchase since loading:
        // purchase() then resumes that one instead of charging again.
        unresolvedPurchase.value = purchaseRecovery.load();
        const result = await purchaseRecovery.purchase(pack.key);
        await updatePurchaseResult(result);
        if (result?.status === 'pending') startPurchasePolling();
    } catch {
        // This includes failed storage writes: never claim that no charge occurred
        // when the browser simply could not confirm the outcome.
        purchaseError.value = _mft('dashboard:aiSpend.purchaseRecoveryFailed');
    } finally {
        try {
            unresolvedPurchase.value = purchaseRecovery.load();
        } catch {
            recoveryReady.value = false;
        }
        purchasingPack.value = null;
        pendingPack.value = null;
    }
}

async function openPaymentMethodSetup() {
    if (openingBilling.value) return;
    openingBilling.value = true;
    billingError.value = '';
    try {
        // Stripe hosts both flows: the billing portal replaces the card of an
        // existing customer, Checkout in setup mode stores the very first one.
        const response = await fetch(
            hasStripeCustomer.value
                ? route('billing.subscription.portal')
                : route('billing.subscription.cardSetup'),
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken(),
                },
                // Leaving the urls out keeps the server defaults, which return to
                // company settings and store the card there even if the Stripe
                // webhook is delayed.
                body: '{}',
            },
        );
        const data = await response.json();
        if (!response.ok || !data.url) throw new Error('Billing url unavailable');
        // A new tab leaves this modal and any pending purchase untouched; the
        // poll below flips `can_purchase` as soon as the card is saved.
        window.open(data.url, '_blank', 'noopener');
        startPaymentMethodPolling();
    } catch {
        billingError.value = _mft('dashboard:aiSpend.addPaymentMethodFailed');
    } finally {
        openingBilling.value = false;
    }
}

function openPurchaseSection() {
    purchaseCompleted.value = false;
    completedPurchase.value = null;
    showPurchaseSection.value = true;
}

function closePurchaseSection() {
    if (purchasingPack.value !== null) return;
    pendingPack.value = null;
    showPurchaseSection.value = false;
}

function scrollToPurchase() {
    purchaseSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

let activeRequest = null;
let isUnmounted = false;
let purchasePollTimer = null;
let paymentMethodPollTimer = null;

const POLL_INTERVAL_MS = 5000;
const POLL_WINDOW_MS = 5 * 60 * 1000;

function startPurchasePolling() {
    clearTimeout(purchasePollTimer);
    const deadline = Date.now() + POLL_WINDOW_MS;

    const poll = async () => {
        if (isUnmounted || !unresolvedPurchase.value || Date.now() >= deadline) return;

        const result = await recoverPurchase();
        if ((result?.status === 'pending' || result === undefined) && !isUnmounted) {
            purchasePollTimer = setTimeout(poll, POLL_INTERVAL_MS);
        }
    };

    purchasePollTimer = setTimeout(poll, POLL_INTERVAL_MS);
}

function startPaymentMethodPolling() {
    clearTimeout(paymentMethodPollTimer);
    const deadline = Date.now() + POLL_WINDOW_MS;

    const poll = async () => {
        if (isUnmounted || canPurchase.value || Date.now() >= deadline) return;

        try {
            await fetchPacks(true);
        } catch {
            // A temporary refresh failure should not end the setup recovery window.
        }

        if (!canPurchase.value && !isUnmounted) {
            paymentMethodPollTimer = setTimeout(poll, POLL_INTERVAL_MS);
        }
    };

    paymentMethodPollTimer = setTimeout(poll, POLL_INTERVAL_MS);
}

async function fetchData() {
    activeRequest?.abort();

    const controller = new AbortController();
    activeRequest = controller;

    try {
        const response = await fetch(route('boIndex.aiUsage'), {
            signal: controller.signal,
        });

        if (!response.ok) {
            throw new Error('Failed to load AI usage data');
        }

        const data = await response.json();

        if (isUnmounted || activeRequest !== controller) {
            return;
        }

        plan.value = data.plan ?? 'basic';
        included.value = data.included ?? { used_percent: 0, resets_in_days: null };
        purchased.value = data.purchased ?? { used_percent: 0, has_credits: false };
        credits.value = data.credits ?? { balance: 0, expires_next: null };
        canPurchase.value = Boolean(data.can_purchase);
        isCompanyAdmin.value = Boolean(data.is_company_admin);
        serverLimitReached.value = Boolean(data.limit_reached);
        bySpecialist.value = data.by_specialist ?? {};
        recentLogs.value = Array.isArray(data.recent) ? data.recent : [];
        if (isCompanyAdmin.value) {
            fetchPacks()
                .then(() => recoverPurchase())
                .then((result) => {
                    if (result?.status === 'pending') startPurchasePolling();
                })
                .catch(() => {
                    recoveryReady.value = false;
                    purchaseError.value = _mft('dashboard:aiSpend.purchaseRecoveryFailed');
                });
        }
        loading.value = false;
    } catch (e) {
        if (e.name === 'AbortError') {
            return;
        }

        if (!isUnmounted) {
            loading.value = false;
        }
    } finally {
        if (activeRequest === controller) {
            activeRequest = null;
        }
    }
}

onMounted(() => {
    isUnmounted = false;
    fetchData();
});

onUnmounted(() => {
    isUnmounted = true;
    clearTimeout(purchasePollTimer);
    clearTimeout(paymentMethodPollTimer);
    activeRequest?.abort();
    activeRequest = null;
});
</script>

<template>
    <Teleport to="body">
        <div class="fixed inset-0 z-[9998] flex items-center justify-center p-4">
            <!-- Backdrop -->
            <div
                class="absolute inset-0 bg-black/50 backdrop-blur-sm"
                @click="emit('close')"
            ></div>

            <!-- Modal -->
            <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden">
                <!-- Header -->
                <div class="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                    <div>
                        <h2 class="text-lg font-semibold text-gray-900">
                            <mf-translate domainkey="dashboard:aiSpend.label"></mf-translate>
                        </h2>
                        <p class="text-sm text-gray-500 mt-0.5 capitalize">{{ plan }} plan</p>
                    </div>
                    <button-base
                        @click="emit('close')"
                        style-type="default"
                        size="xs"
                        class="!border-0 !bg-transparent !px-2 !py-2 !text-gray-400"
                    >
                        <template #icon>
                            <i class="fa-light fa-xmark text-lg"></i>
                        </template>
                    </button-base>
                </div>

                <!-- Content -->
                <div class="flex-1 overflow-y-auto px-6 py-5">
                    <div v-if="loading" class="flex items-center justify-center py-16">
                        <i class="fa-light fa-spinner-third fa-spin text-3xl text-gray-400"></i>
                    </div>

                    <template v-else>
                        <!-- Purchase confirmation -->
                        <div
                            v-if="purchaseCompleted"
                            class="mb-3 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-800"
                        >
                            <i class="fa-light fa-circle-check mt-0.5"></i>
                            <span>
                                <mf-translate
                                    v-if="completedPurchaseArgs"
                                    domainkey="dashboard:aiSpend.purchaseSucceededPack"
                                    :args="completedPurchaseArgs"
                                ></mf-translate>
                                <mf-translate v-else domainkey="dashboard:aiSpend.purchaseSucceeded"></mf-translate>
                            </span>
                        </div>
                        <confetti-bomb v-if="purchaseCompleted" :key="confettiKey" :z-index="9999" />

                        <!-- Included monthly usage -->
                        <card-sub class="">
                            <div class="flex items-end justify-between mb-3">
                                <div>
                                    <div class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                        <mf-translate domainkey="dashboard:aiSpend.includedUsage"></mf-translate>
                                    </div>
                                </div>
                                <span
                                    v-if="limitReached"
                                    role="button"
                                    tabindex="0"
                                    class="text-sm font-medium text-red-600 bg-red-50 px-2.5 py-1 rounded-full"
                                    @click="scrollToPurchase"
                                    @keydown.enter="scrollToPurchase"
                                >
                                    <mf-translate domainkey="dashboard:aiSpend.limitReached"></mf-translate>
                                </span>
                                <span v-else class="text-sm text-gray-500">
                                    {{ usedPercent(includedPercentage) }}% <mf-translate domainkey="dashboard:aiSpend.used"></mf-translate>
                                </span>
                            </div>
                            <div class="w-full h-3 rounded-full overflow-hidden bg-gray-200">
                                <div
                                    class="h-full rounded-full transition-all duration-500"
                                    :class="includedPercentage >= 100 ? 'bg-myflowRed' : 'bg-myflowPurple'"
                                    :style="{ width: includedPercentage + '%' }"
                                ></div>
                            </div>
                            <div v-if="resetsInDays" class="mt-1 text-xs text-gray-500">
                                {{ _mft('dashboard:aiSpend.resetsIn', { days: resetsInDays }) }}
                            </div>
                        </card-sub>

                        <!-- Purchased usage -->
                        <card-sub class=" mt-3">
                            <div class="flex items-end justify-between mb-3">
                                <div>
                                    <div class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        <mf-translate domainkey="dashboard:aiSpend.purchasedUsage"></mf-translate>
                                    </div>
                                </div>
                                <span v-if="hasPurchasedUsage" class="text-sm text-gray-500">
                                    {{ usedPercent(purchasedPercentage) }}% <mf-translate domainkey="dashboard:aiSpend.used"></mf-translate>
                                </span>
                            </div>
                            <div class="w-full h-3 rounded-full overflow-hidden bg-gray-200">
                                <div
                                    class="h-full rounded-full transition-all duration-500"
                                    :class="purchasedPercentage >= 100 ? 'bg-myflowRed' : 'bg-myflowGreen-700'"
                                    :style="{ width: purchasedPercentage + '%' }"
                                ></div>
                            </div>
                            <div v-if="hasPurchasedUsage && credits.expires_next" class="mt-1 text-xs text-gray-500">
                                <mf-translate domainkey="dashboard:aiSpend.nextExpiry"></mf-translate>
                                {{ formatDate(credits.expires_next) }}
                            </div>
                            <div v-if="! hasPurchasedUsage" class="mt-1 text-xs text-gray-400">
                                <mf-translate domainkey="dashboard:aiSpend.noPurchasedUsage"></mf-translate>
                            </div>
                        </card-sub>
                        <div class="flex justify-end mt-3">
                            <button-base
                                v-if="showPurchaseSection === false"
                                :style-type="includedPercentage >= 100 && (purchasedPercentage >= 100 || ! hasPurchasedUsage) ? 'primary' : 'secondary'"
                                size="sm"
                                @click="openPurchaseSection"
                            >
                                {{
                                    includedPercentage >= 100 && purchasedPercentage >= 100 ?
                                        _mft('dashboard:aiSpend.buyCreditsToContinue')
                                        :
                                        _mft('dashboard:aiSpend.buyCredits')

                                }}
                            </button-base>
                        </div>


                        <div
                            v-if="showPurchaseSection === true"
                            ref="purchaseSection"
                            class="scroll-mt-4 mt-6"
                        >
                            <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                <mf-translate domainkey="dashboard:aiSpend.buyCredits"></mf-translate>
                            </h3>
                            <template v-if="isCompanyAdmin">
                                <!-- Confirmation step, replacing the pack list -->
                                <div
                                    v-if="pendingPack"
                                    class="rounded-xl border border-blue-200 bg-blue-50 p-4"
                                >
                                    <div class="text-sm font-semibold text-gray-900">
                                        <mf-translate domainkey="dashboard:aiSpend.confirmPurchase"></mf-translate>
                                        {{ formatCurrency(pendingPack.amount_cents, pendingPack.currency) }}
                                        (<mf-translate domainkey="dashboard:aiSpend.excludingVat"></mf-translate>)
                                    </div>
                                    <p class="mt-2 text-xs text-gray-500">
                                        <mf-translate domainkey="dashboard:aiSpend.validFor"></mf-translate>
                                        {{ pendingPack.validity_months }}
                                        <mf-translate domainkey="dashboard:aiSpend.months"></mf-translate>.
                                        <mf-translate domainkey="dashboard:aiSpend.chargedImmediately"></mf-translate>
                                    </p>
                                    <div class="mt-4 flex items-center justify-end gap-2">
                                        <button-secondary
                                            :disabled="purchasingPack !== null"
                                            @click="cancelPendingPurchase"
                                        >
                                            <mf-translate domainkey="shared:action.cancel"></mf-translate>
                                        </button-secondary>
                                        <button-base
                                            style-type="primary"
                                            :disabled="!canPurchase || !recoveryReady || purchasingPack !== null"
                                            :state="purchasingPack !== null ? 'waiting' : ''"
                                            @click="confirmPendingPurchase"
                                        >
                                            {{ purchasingPack !== null ? _mft('shared:action.purchasing') : _mft('shared:action.purchase') }}
                                        </button-base>
                                    </div>
                                </div>

                                <div v-else class="grid gap-3 sm:grid-cols-3">
                                    <button
                                        v-for="pack in packs"
                                        :key="pack.key"
                                        type="button"
                                        class="rounded-xl border border-gray-200 p-4 text-left transition hover:border-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
                                        :disabled="!canPurchase || !recoveryReady || unresolvedPurchase !== null || purchasingPack !== null"
                                        @click="requestPurchase(pack)"
                                    >
                                        <span class="block text-sm font-semibold text-myflowGreen">{{ pack.name }}</span>
                                        <span class="mt-1 block text-lg font-bold text-gray-900">
                                            {{ formatCurrency(pack.amount_cents, pack.currency) }}
                                        </span>
                                        <span class="block text-xs text-gray-400">
                                            <mf-translate domainkey="dashboard:aiSpend.excludingVat"></mf-translate>
                                        </span>
                                    </button>
                                </div>
                            </template>
                            <p v-if="!isCompanyAdmin" class="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                                <mf-translate domainkey="dashboard:aiSpend.askAdmin"></mf-translate>
                            </p>
                            <div v-if="isCompanyAdmin && !canPurchase" class="mt-3 text-sm text-amber-700">
                                <mf-translate domainkey="dashboard:aiSpend.paymentMethodRequired"></mf-translate>
                                <button
                                    type="button"
                                    class="mf-link ml-1"
                                    :disabled="openingBilling"
                                    @click="openPaymentMethodSetup"
                                >
                                    <mf-translate domainkey="dashboard:aiSpend.addPaymentMethod"></mf-translate>
                                </button>
                            </div>
                            <p v-if="billingError" class="mt-2 text-sm text-red-700">{{ billingError }}</p>
                            <button
                                v-if="unresolvedPurchase && !pendingPack"
                                type="button"
                                class="mf-link mt-3"
                                :disabled="purchasingPack !== null"
                                @click="recoverPurchase(true)"
                            >
                                <mf-translate domainkey="dashboard:aiSpend.resumePurchase"></mf-translate>
                            </button>
                            <p v-if="purchaseMessage" class="mt-3 text-sm text-green-700">{{ purchaseMessage }}</p>
                            <p v-if="purchaseError" class="mt-3 text-sm text-red-700">{{ purchaseError }}</p>

                            <div v-if="!pendingPack" class="mt-2 flex justify-end">
                                <button-secondary @click="closePurchaseSection">
                                    <mf-translate domainkey="shared:action.cancel"></mf-translate>
                                </button-secondary>
                            </div>
                        </div>

                        <div v-if="showPurchaseSection === false" class="mt-4">
                            <ui-tabbed>
                                <ui-tabbed-tab
                                    value="specialist-breakdown"
                                    :title="_mft('dashboard:aiSpend.breakdown')"
                                    :active="true"
                                >
                                    <!-- Per-specialist breakdown -->
                                    <div v-if="specialists.length > 0">
                                        <div class="grid gap-3" :class="specialists.length > 1 ? 'grid-cols-2' : 'grid-cols-1'">
                                            <div
                                                v-for="s in specialists"
                                                :key="s.key"
                                                class="border border-gray-200 rounded-xl p-4"
                                            >
                                                <div class="flex items-center gap-3 mb-3">
                                                    <div class="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50">
                                                        <component :is="s.avatar" class="w-full h-full"></component>
                                                    </div>
                                                    <div>
                                                        <div class="text-sm font-semibold text-gray-900">{{ s.name }}</div>
                                                        <div class="text-xs text-gray-500">{{ s.description }}</div>
                                                    </div>
                                                </div>
                                                <div class="text-xl font-bold text-gray-900 mb-1">{{ s.ofLimit }}%</div>
                                                <div class="flex items-center gap-3 text-xs text-gray-500">
                                                    <span>{{ s.requests }} <mf-translate domainkey="dashboard:aiSpend.requests"></mf-translate></span>
                                                    <span class="text-gray-300">|</span>
                                                    <span>{{ s.percentage }}% <mf-translate domainkey="dashboard:aiSpend.ofTotal"></mf-translate></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Empty state -->
                                    <div v-if="specialists.length === 0" class="text-center py-8 mt-3">
                                        <i class="fa-light fa-chart-pie-simple text-4xl text-gray-300 mb-3 block"></i>
                                        <p class="text-sm text-gray-500">
                                            <mf-translate domainkey="dashboard:aiSpend.noUsage"></mf-translate>
                                        </p>
                                    </div>
                                </ui-tabbed-tab>
                                <ui-tabbed-tab
                                    value="recent-activity"
                                    :title="_mft('dashboard:aiSpend.recentActivity')"
                                >
                                    <!-- Recent activity -->
                                    <div v-if="recentLogs.length > 0">
                                        <div class="border border-gray-200 rounded-xl overflow-hidden">
                                            <table class="w-full text-sm">
                                                <thead>
                                                    <tr class="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                                        <th class="text-left px-4 py-2.5 font-medium">Specialist</th>
                                                        <th class="text-right px-4 py-2.5 font-medium"><mf-translate domainkey="dashboard:aiSpend.usage"></mf-translate></th>
                                                        <th class="text-right px-4 py-2.5 font-medium"><mf-translate domainkey="dashboard:aiSpend.when"></mf-translate></th>
                                                    </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-100">
                                                    <tr
                                                        v-for="(log, i) in recentLogs"
                                                        :key="log.id ?? `${log.specialist}-${log.created_at}-${i}`"
                                                        class="hover:bg-gray-50 transition-colors"
                                                    >
                                                        <td class="px-4 py-2.5">
                                                            <span class="font-medium text-gray-700">{{ specialistNames[log.specialist] || log.specialist }}</span>
                                                        </td>
                                                        <td class="px-4 py-2.5 text-right font-medium text-gray-900 flex justify-end items-center">
                                                            {{ pct(log.share_percent) }}
                                                            <div
                                                                class="w-2 h-2 rounded-full ml-1"
                                                                :class="log.funded_by === 'credits' ? 'bg-green-600' : 'bg-myflowPurple'"
                                                                :title="log.funded_by === 'credits' ? _mft('dashboard:aiSpend.purchasedUsage') : _mft('dashboard:aiSpend.includedUsage')"
                                                            >
                                                            </div>
                                                        </td>
                                                        <td class="px-4 py-2.5 text-right text-gray-400">
                                                            {{ formatTime(log.created_at) }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </ui-tabbed-tab>
                            </ui-tabbed>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </Teleport>
</template>
